import type { SimplifiedGroup } from '../group/group.utils';
import { formatGroupName } from '../group/group.utils';

export interface SimplifiedUser {
  id: string;
  displayName?: string | null;
  customCallSign?: string | null;
  callSign?: string | null;
  lastName?: string | null;
  firstName?: string | null;
  username?: string | null;
  email?: string | null;
}

export function getUserDisplayName(user?: SimplifiedUser, fallback = 'N/A'): string {
  const displayName = getUserDisplayNameOrUndefined(user);

  return displayName ?? fallback;
}

export function getUserDisplayNameOrUndefined(user?: SimplifiedUser): string | undefined {
  if (user?.customCallSign) {
    return user.customCallSign;
  }

  const callSign = user?.callSign?.trim();

  if (callSign) {
    return callSign;
  }

  const firstName = user?.firstName?.trim();
  const lastName = user?.lastName?.trim();

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  const username = user?.username?.trim();

  if (username) {
    return username;
  }

  return undefined;
}

type MemberList = { user?: SimplifiedUser; group?: SimplifiedGroup }[];

export function formatMembers(
  members: MemberList,
  loggedInUserId?: string,
  maxMembers = 3,
  totalMembers = 0,
): string {
  if (!members) {
    return 'N/A';
  }

  const filteredMembers
    = members.length > 1
      ? members.filter(userOrGroup => userOrGroup.user?.id !== loggedInUserId)
      : members;

  const numMembers = totalMembers || filteredMembers.length;

  if (numMembers > maxMembers) {
    return `${filteredMembers
      .slice(0, maxMembers)
      .map(userOrGroup => formatName(userOrGroup.user ?? userOrGroup.group))
      .join(', ')} +${numMembers - maxMembers}`;
  }

  return filteredMembers
    .map(userOrGroup => formatName(userOrGroup.user ?? userOrGroup.group))
    .join(', ');
}

export function formatName(userOrGroup?: SimplifiedGroup | SimplifiedUser): string | undefined {
  if (!userOrGroup) {
    return 'N/A';
  }

  if (isUser<SimplifiedUser>(userOrGroup)) {
    return getUserDisplayNameOrUndefined(userOrGroup);
  }

  return formatGroupName(userOrGroup);
}

export function isUser<T extends object>(
  inputToCheck: object,
): inputToCheck is T {
  return 'email' in inputToCheck;
}

export class UserUtils {
  static hasId<T, U>(user: T & { id?: U | undefined }): user is T & { id: U } {
    return user.id !== undefined;
  }
}
